import { Box, Card, CircularProgress, Grid, Icon, IconButton, InputLabel, Skeleton, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDFunctionalityModal from "components/MDFunctionalityModal";
import MDTypography from "components/MDTypography";
import { Form, Formik, useFormikContext } from "formik";
import FormField from "layouts/pages/users/new-user/components/FormField";
import PropTypes from "prop-types";
import { memo, useEffect, useState } from 'react';
import { menus } from "utils/Enums";
import { errorAlert, successAlert } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";
import MDSelect from "components/MDSelect";
import * as Yup from 'yup';
import { useUser } from "context/UserContext";

const ProfileCreateEdit = ({ profileId, closeCreateEdit, refreshTable, createCopy }) => {

    const {
        nome,
        descricao, } = configForm.formField

    const { tipoPerfilEnum, setUserFunctionalities } = useUser();

    const [menusList, setMenusList] = useState([]);
    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    const [isLoadingProfileMenus, setIsLoadingProfileMenus] = useState(false);
    const [functionalityModalOpen, setFunctionalityModalOpen] = useState(false);

    useEffect(() => {
        setIsLoadingProfileMenus(true);
        get(`/profiles/menus`).then(data => {
            setMenusList(mapFunctionalityNames(data.list, menus));
            setIsLoadingProfileMenus(false);
        }).catch(() => {
            setMenusList([]);
            setIsLoadingProfileMenus(false);
        });
    }, []);

    function mapFunctionalityNames(apiResponse, enumMenus) {
        const apiMap = new Map(apiResponse.map(item => [item.name, item]));

        const finalList = Object.entries(enumMenus).map(([code, enumItem]) => {
            const apiItem = apiMap?.get(code);

            return {
                code,
                name: enumItem.name || 'Nome não encontrado',
                menu: enumItem.menu || 'Outros',
                description: enumItem.description || '',
                ...(apiItem || {})
            };
        });

        return finalList;
    }

    const handleSubmit = (values, actions) => {
        try {
            const idProfile = createCopy ? null : values.id
            const profile = {
                id: idProfile,
                nome: values.nome,
                descricao: values.descricao,
                associaCliente: values.associaCliente,
                ativo: values.ativo,
                funcionalidades: values.funcionalidades,
                tipoPerfil: values.tipoPerfil?.code
            };
            
            postNoResponse("/profiles/save", profile)
            .then(() => {
                successAlert(!profileId ? "Perfil criado com sucesso!" : "Alterações salvas!", () => {
                    handleSucess();
                    // Atualiza as permissões na hora sem precisar deslogar!
                    get('/users/by-token').then(({ funcionalidades }) => {
                        if (funcionalidades) {
                            setUserFunctionalities(funcionalidades);
                        }
                    }).catch(() => null);
                });
            })
            .catch(() => {
                errorAlert(!profileId ? "Erro ao criar perfil!" : 'Erro ao salvar alterações.');
            })
            .finally(() => {
                actions.setSubmitting(false);
            });
        } catch (error) {
            console.error(error);
            errorAlert("Erro inesperado ao salvar!");
            actions.setSubmitting(false);
        }
    };

    function handleSucess() {
        closeCreateEdit()
        refreshTable()
    }

    const getSelectedFunctionalityNames = (selectedCodes) => {
        if (!selectedCodes || selectedCodes.length === 0) return 'Nenhuma funcionalidade selecionada';

        const selectedMenus = menusList.filter(menu => selectedCodes.includes(menu.code));
        if (selectedMenus.length === 0) return 'Nenhuma funcionalidade selecionada';

        if (selectedMenus.length === 1) {
            return selectedMenus[0].name;
        } else if (selectedMenus.length <= 3) {
            return selectedMenus.map(menu => menu.name).join(', ');
        } else {
            return `${selectedMenus.length} funcionalidades selecionadas`;
        }
    };

    return (
        <Card id="user-info">
            <Box display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1, paddingTop: 0.75 }}>
                    <MDTypography variant="h5">
                        <Icon>arrow_back</Icon>
                    </MDTypography>
                </IconButton>
                <MDTypography variant="h6">{profileId ? "Editar" : "Adicionar"} Perfil</MDTypography>
            </Box>
            <Formik
                initialValues={{
                    id: null,
                    nome: '',
                    descricao: '',
                    associaCliente: false,
                    ativo: true,
                    funcionalidades: null,
                    tipoPerfil: null
                }}
                validationSchema={validationSchema}
                onSubmit={(values, actions) => {
                    actions.setSubmitting(true);
                    handleSubmit(values, actions);
                }}>
                {({ values, errors, touched, isSubmitting, setFieldValue, setFieldTouched  }) => {
                    return (
                        <Form id={configForm.formId} autoComplete="off">
                            <FetchAndSetData
                                profileId={profileId}
                                setIsLoadingInfo={setIsLoadingInfo}
                                closeCreateEdit={closeCreateEdit} 
                                tipoPerfilEnum={tipoPerfilEnum}
                            />

                            <MDBox px={3}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={nome.type}
                                            label={nome.label}
                                            name={nome.name}
                                            value={values.nome}
                                            placeholder={nome.placeholder}
                                            error={errors.nome?.length > 0 && touched.nome}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={descricao.type}
                                            label={descricao.label}
                                            name={descricao.name}
                                            value={values.descricao}
                                            placeholder={descricao.placeholder}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                      <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>
                                        Tipo de Perfil
                                      </InputLabel>
                                      <MDSelect
                                        name="tipoPerfil"
                                        placeholder="Selecione o tipo de perfil"
                                        getOptionValue={(option) => option.code}
                                        getOptionLabel={(option) => option.name}
                                        value={values.tipoPerfil}
                                        options={tipoPerfilEnum}
                                        onChange={(option) => setFieldValue("tipoPerfil", option)}
                                        onBlur={() => setFieldTouched("tipoPerfil", true)}
                                        error={touched.tipoPerfil && Boolean(errors.tipoPerfil)}
                                      />
                                      {touched.tipoPerfil && errors.tipoPerfil && (
                                        <MDTypography variant="caption" color="error">
                                          {errors.tipoPerfil}
                                        </MDTypography>
                                      )}
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Funcionalidades</InputLabel>
                                        <Box
                                            sx={{
                                                border: '1px solid #d2d6da',
                                                borderRadius: '0.5rem',
                                                padding: '0 12px',
                                                minHeight: '30px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                backgroundColor: isLoadingInfo || isLoadingProfileMenus ? '#f5f5f5' : 'white',
                                                '&:hover': {
                                                    borderColor: '#1976d2',
                                                },
                                                ...(errors.funcionalidades && touched.funcionalidades && {
                                                    borderColor: '#d32f2f',
                                                })
                                            }}
                                            onClick={() => {
                                                if (!isLoadingInfo && !isLoadingProfileMenus) {
                                                    setFunctionalityModalOpen(true);
                                                }
                                            }}
                                        >
                                            <MDTypography
                                                variant="body2"
                                                sx={{ fontSize: '10px' }}
                                            >
                                                {isLoadingInfo || isLoadingProfileMenus
                                                    ? "Carregando..."
                                                    : getSelectedFunctionalityNames(values.funcionalidades)
                                                }
                                            </MDTypography>
                                            <Icon sx={{ color: '#666', fontSize: '16px !important' }}>expand_more</Icon>
                                        </Box>
                                        {errors.funcionalidades && touched.funcionalidades && (
                                            <MDTypography variant="caption" color="error">
                                                {errors.funcionalidades}
                                            </MDTypography>
                                        )}
                                    </Grid>


                                    <Grid item xs={12} sm={2} style={{ display: 'flex' }}>
                                        {isLoadingInfo ? <Skeleton width={"80%"} sx={{ mt: 2 }} /> :
                                            <Box display="flex" justifyContent="start" alignItems="center" lineHeight={1}>
                                                <MDTypography variant="button">
                                                    Associa Cliente
                                                </MDTypography>
                                                <Box ml={1}>
                                                    <Switch checked={values.associaCliente}
                                                        onChange={(event) => setFieldValue('associaCliente', event.target.checked)} />
                                                </Box>
                                            </Box>
                                        }
                                    </Grid>

                                    <Grid item xs={12} sm={2} style={{ display: 'flex' }}>
                                        {isLoadingInfo ? <Skeleton width={"80%"} sx={{ mt: 2 }} /> :
                                            <Box display="flex" justifyContent="end" alignItems="center" lineHeight={1}>
                                                <MDTypography variant="button">
                                                    Ativo
                                                </MDTypography>
                                                <Box ml={1}>
                                                    <Switch checked={values.ativo || false}
                                                        onChange={(event) => setFieldValue('ativo', event.target.checked)} />
                                                </Box>
                                            </Box>
                                        }
                                    </Grid>

                                </Grid>
                            </MDBox>
                            <Box display="flex" alignItems="center" justifyContent="end" p={2}>
                                <MDButton
                                    disabled={isLoadingInfo
                                        || isLoadingProfileMenus}
                                    size="large"
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    type="submit"
                                    endIcon={isSubmitting ?
                                        <CircularProgress
                                            color="inherit"
                                            size={20} sx={{ ml: 0, mr: 0 }} /> : null}>
                                    {!isSubmitting && (profileId ? "Salvar" : "Adicionar")}
                                </MDButton>
                            </Box>

                            <MDFunctionalityModal
                                open={functionalityModalOpen}
                                onClose={() => setFunctionalityModalOpen(false)}
                                menusList={menusList}
                                selectedFunctionalities={values.funcionalidades || []}
                                onSave={(selectedCodes) => {
                                    setFieldValue('funcionalidades', selectedCodes);
                                }}
                            />
                        </Form>)
                }}
            </Formik>
        </Card>
    );
};

ProfileCreateEdit.propTypes = {
    profileId: PropTypes.string,
    closeCreateEdit: PropTypes.func.isRequired,
};

export default memo(ProfileCreateEdit);

const FetchAndSetData = ({ profileId, setIsLoadingInfo, closeCreateEdit, tipoPerfilEnum }) => {
    const { setFieldValue } = useFormikContext();

    useEffect(() => {
        if (profileId) {
            setIsLoadingInfo(true);
            get(`/profiles/by-id/${profileId}`).then(data => {
                const { id, nome, descricao, associaCliente, ativo, funcionalidades, tipoPerfil } = data;
                setFieldValue('funcionalidades', funcionalidades);
                setFieldValue('associaCliente', associaCliente);
                setFieldValue('descricao', descricao);
                setFieldValue('ativo', ativo);
                setFieldValue('nome', nome);
                setFieldValue('id', id);
                setFieldValue('tipoPerfil', tipoPerfilEnum?.find(ht => ht.code === tipoPerfil))
                setIsLoadingInfo(false);
            }).catch((erro) => errorAlert("Perfil não encontrado!", closeCreateEdit));
        }
    }, [profileId]);

    return null;
};

export const configForm = {
    formId: "configuration-form",
    formField: {
        nome: {
            name: "nome",
            label: "Nome",
            placeholder: "Informe o nome...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",
        },
        descricao: {
            name: "descricao",
            label: "Descrição",
            placeholder: "Informe a descrição...",
        },
        funcionalidades: {
            name: "funcionalidades",
            label: "Funcionalidades",
            placeholder: "Selecione as funcionalidades",
            errorMsg: "Campo Obrigatório.",
        },
    }
}

const {
    formField: {
        nome,
        funcionalidades
    },
} = configForm;

export const validationSchema = Yup.object().shape({
    [nome.name]: Yup.string()
        .required(nome.errorMsg),
    [funcionalidades.name]: Yup.array()
        .required(funcionalidades.errorMsg),
    tipoPerfil: Yup.object()
    .nullable()
    .required('Campo obrigatório.'),
});
